import { useCallback, useEffect } from "react";
import { signInWithCustomToken } from "@firebase/auth";
import { useRouter } from "next/router";

import logger from "@olivahealth/logger/client";
import { useAuth } from "@olivahealth/firebase/client";
import { getGraphqlUrl } from "@olivahealth/utils/relayUtils";
import {
  SSO_MULTI_ORG_LOCAL_STORAGE_KEY,
  TRACK_EVENT_SSO_PROVIDER_NAME,
} from "@olivahealth/constants";

import { useAmplitude } from "../services/contexts/AmplitudeContext";
import { LoginErrorType } from "../types/tracking";
import useOAuthClient from "./useOAuthClient";

export type AuthAuthorizedType =
  | "loading"
  | "success"
  | "in_progress"
  | "error";

const SSO_EXCHANGE_TIMEOUT_MS = 8_000;

class SsoTimeoutError extends Error {
  readonly stage: string;
  constructor(stage: string, ms: number) {
    super(`SSO ${stage} timed out after ${ms}ms`);
    this.name = "SsoTimeoutError";
    this.stage = stage;
  }
}

const withTimeout = <T>(
  promise: Promise<T>,
  ms: number,
  stage: string,
): Promise<T> => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  return Promise.race<T>([
    promise.finally(() => clearTimeout(timeoutId)),
    new Promise<never>((_, reject) => {
      timeoutId = setTimeout(() => reject(new SsoTimeoutError(stage, ms)), ms);
    }),
  ]);
};

const getSavedOrganisationId = (): string | null | undefined => {
  try {
    return localStorage.getItem(SSO_MULTI_ORG_LOCAL_STORAGE_KEY);
  } catch (error) {
    logger.error(
      "getSavedOrganisationId",
      "Failed to get saved organisation id",
      {
        errorMessage: (error as Error).message,
        errorStack: (error as Error).stack,
      },
    );
  }
};

const fetchCustomLoginToken = async (
  tokenValue: string,
  savedOrganisationId: string | null | undefined,
): Promise<string | undefined> => {
  const url = `${getGraphqlUrl()}/sso/login?accessToken=${tokenValue}${
    savedOrganisationId ? `&organisationId=${savedOrganisationId}` : ""
  }`;

  const controller = new AbortController();
  const timeoutId = setTimeout(
    () => controller.abort(),
    SSO_EXCHANGE_TIMEOUT_MS,
  );

  try {
    const response = await fetch(url, { signal: controller.signal });

    if (!response.ok) {
      let errorDetails: { error?: string; code?: string; details?: string } =
        {};
      try {
        errorDetails = await response.json();
      } catch {
        // Response wasn't JSON, use status text
        errorDetails = { error: response.statusText };
      }

      logger.error(
        "useLoginReturningFromAuthServer",
        "SSO login endpoint returned error",
        {
          status: response.status,
          statusText: response.statusText,
          errorCode: errorDetails.code,
          errorMessage: errorDetails.error,
          errorDetails: errorDetails.details,
        },
      );
      return undefined;
    }

    const data = await response.json();
    return data?.token;
  } catch (error) {
    if ((error as Error).name === "AbortError") {
      throw new SsoTimeoutError(
        "fetchCustomLoginToken",
        SSO_EXCHANGE_TIMEOUT_MS,
      );
    }
    logger.error(
      "useLoginReturningFromAuthServer",
      "Failed to fetch custom login token - network error",
      {
        errorMessage: (error as Error).message,
        errorStack: (error as Error).stack,
      },
    );
  } finally {
    clearTimeout(timeoutId);
  }
};

export default function useLoginReturningFromAuthServer(
  setAuthStatus: (status: AuthAuthorizedType) => void,
) {
  const oAuthClient = useOAuthClient();
  const auth = useAuth();
  const router = useRouter();
  const { trackEvent } = useAmplitude();

  const removeAuthCodeAndStateFromUrl = useCallback(async () => {
    const { query } = router;
    delete query.code;
    delete query.state;
    await router.replace({ pathname: router.pathname, query });
  }, [router]);

  useEffect(() => {
    const { query } = router;
    const hasAuthParams = Boolean(query.code && query.state);

    if (hasAuthParams) {
      setAuthStatus("loading");
    }
  }, [router, setAuthStatus]);

  useEffect(() => {
    const trackLoginError = (errorType: LoginErrorType) => {
      trackEvent("Login error occurred", {
        provider: TRACK_EVENT_SSO_PROVIDER_NAME,
        errorType,
      });
    };

    const loginUserReturningFromAuthServer = async () => {
      try {
        const { query } = router;
        const hasAuthParams = Boolean(query.code && query.state);

        if (!oAuthClient || !hasAuthParams) {
          return;
        }

        const shouldTrySSOLogin = await oAuthClient.isReturningFromAuthServer();

        if (!shouldTrySSOLogin) {
          return setAuthStatus("success");
        }

        const token = await withTimeout(
          oAuthClient.getAccessToken(),
          SSO_EXCHANGE_TIMEOUT_MS,
          "getAccessToken",
        );
        const accessToken = token?.token?.value;

        if (!accessToken) {
          logger.error(
            "useLoginReturningFromAuthServer",
            "SSO login failed - no access token from OAuth provider",
          );
          trackLoginError(LoginErrorType.SSO_NO_ACCESS_TOKEN);
          await removeAuthCodeAndStateFromUrl();
          return setAuthStatus("error");
        }

        try {
          const savedOrganisationId = getSavedOrganisationId();
          const customLoginToken = await fetchCustomLoginToken(
            accessToken,
            savedOrganisationId,
          );

          if (!customLoginToken) {
            logger.error(
              "useLoginReturningFromAuthServer",
              "SSO login failed - no custom login token returned from server",
            );
            trackLoginError(LoginErrorType.SSO_NO_CUSTOM_LOGIN_TOKEN);
            await removeAuthCodeAndStateFromUrl();
            return setAuthStatus("error");
          }

          await withTimeout(
            signInWithCustomToken(auth, customLoginToken),
            SSO_EXCHANGE_TIMEOUT_MS,
            "signInWithCustomToken",
          );
          await removeAuthCodeAndStateFromUrl();
          trackEvent("Logged in", { provider: TRACK_EVENT_SSO_PROVIDER_NAME });
          return setAuthStatus("success");
        } catch (error) {
          if (error instanceof SsoTimeoutError) {
            trackLoginError(LoginErrorType.SSO_EXCHANGE_TIMEOUT);
            logger.error(
              "useLoginReturningFromAuthServer",
              "SSO login failed - timeout",
              {
                stage: error.stage,
                timeoutMs: SSO_EXCHANGE_TIMEOUT_MS,
                errorMessage: error.message,
              },
            );
          } else {
            trackLoginError(LoginErrorType.GENERAL_APP_ERROR);
            logger.error(
              "useLoginReturningFromAuthServer",
              "SSO login failed - Firebase signInWithCustomToken error",
              {
                errorMessage: (error as Error).message,
                errorStack: (error as Error).stack,
              },
            );
          }
          await removeAuthCodeAndStateFromUrl();
          return setAuthStatus("error");
        }
      } catch (error) {
        if (error instanceof SsoTimeoutError) {
          trackLoginError(LoginErrorType.SSO_EXCHANGE_TIMEOUT);
          logger.error(
            "useLoginReturningFromAuthServer",
            "SSO login failed - timeout",
            {
              stage: error.stage,
              timeoutMs: SSO_EXCHANGE_TIMEOUT_MS,
              errorMessage: error.message,
            },
          );
        } else {
          trackLoginError(LoginErrorType.GENERAL_APP_ERROR);
          logger.error(
            "useLoginReturningFromAuthServer",
            "SSO login failed - OAuth verification error",
            {
              errorMessage: (error as Error).message,
              errorStack: (error as Error).stack,
            },
          );
        }
        await removeAuthCodeAndStateFromUrl();
        return setAuthStatus("error");
      }
    };

    loginUserReturningFromAuthServer();
  }, [
    auth,
    oAuthClient,
    router,
    setAuthStatus,
    removeAuthCodeAndStateFromUrl,
    trackEvent,
  ]);
}
